/**
 * noteComposerFields - what the person picked or typed in a fresh
 * compose beside the note itself: the band, the hours, the date and After
 * hours. The note's words keep their own localStorage draft
 * (draftAutosave.ts); these ride beside it in sessionStorage, so a door
 * that leaves the record (a Writing tool, a link in the feed) and the
 * Back that returns to it find the note as it was (LP-27).
 *
 * Only what the person set is kept: a band they picked (never the default
 * the composer chose), hours that no longer follow the band, a date other
 * than the default, After hours on. They are kept only while the note has
 * words and restored only with them, so a posted or emptied note leaves
 * nothing behind. Stamped with the person signed in and restored only to
 * them; dropped after DRAFT_TTL_MS, as a note draft is.
 */

import { DRAFT_TTL_MS } from '@/lib/draftAutosave';

export interface NoteComposerFields {
  band?: string;
  hours?: string;
  occurredAt?: string;
  afterHours?: true;
  /** The long-distance tick (LP-46), kept like After Hours. */
  longDistance?: true;
}

interface StoredFields extends NoteComposerFields {
  savedAt: number;
  authorId: string | null;
}

export function noteComposerFieldsKey(tenantSlug: string, clientId: string): string {
  return `note-composer-fields:${tenantSlug}:${clientId}`;
}

export function readNoteComposerFields(key: string, authorId: string | null): NoteComposerFields {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      window.sessionStorage.removeItem(key);
      return {};
    }
    const s = parsed as Partial<StoredFields>;
    if (typeof s.savedAt !== 'number' || Date.now() - s.savedAt > DRAFT_TTL_MS || s.authorId !== authorId) {
      window.sessionStorage.removeItem(key);
      return {};
    }
    const out: NoteComposerFields = {};
    if (typeof s.band === 'string' && s.band !== '') out.band = s.band;
    if (typeof s.hours === 'string') out.hours = s.hours;
    if (typeof s.occurredAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.occurredAt)) out.occurredAt = s.occurredAt;
    if (s.afterHours === true) out.afterHours = true;
    if (s.longDistance === true) out.longDistance = true;
    return out;
  } catch {
    return {};
  }
}

/** Keeps the fields given; nothing set removes the entry. */
export function writeNoteComposerFields(key: string, fields: NoteComposerFields, authorId: string | null): void {
  try {
    if (Object.keys(fields).length === 0) {
      window.sessionStorage.removeItem(key);
      return;
    }
    const stored: StoredFields = { ...fields, savedAt: Date.now(), authorId };
    window.sessionStorage.setItem(key, JSON.stringify(stored));
  } catch {
    // sessionStorage unavailable (private mode): the fields just don't persist.
  }
}
